"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvoiceLineHelper = exports.InvoiceLineCalculator = exports.CurrencyDigitsHelper = void 0;
const debug_1 = __importDefault(require("debug"));
const big_js_1 = __importDefault(require("big.js"));
const lodash_1 = require("lodash");
const handlebars_1 = require("handlebars");
const log = (0, debug_1.default)('itp:integro.helpers.InvoiceLineHelper');
class CurrencyDigitsHelper {
    currency;
    digits;
    constructor(currency) {
        this.update(currency);
    }
    update(currency) {
        this.currency = currency;
        this.digits = {
            default: currency.digits,
            quantity: currency.quantity_digits,
            unitPrice: currency.price_digits,
            amounts: currency.digits
        };
    }
}
exports.CurrencyDigitsHelper = CurrencyDigitsHelper;
class InvoiceLineCalculator {
    _recalculating = false;
    defaultTaxCode;
    defaultDiscount;
    currency;
    taxCodes;
    digits;
    constructor(currency, taxCodes) {
        this.currency = currency;
        this.taxCodes = taxCodes;
        this.digits = new CurrencyDigitsHelper(currency).digits;
    }
    calculateUnitPrice(item, tax, decimals) {
        if (!item.net_amount) {
            return;
        }
        if (!item.net_amount) {
            return;
        }
        const netAmount = (0, big_js_1.default)(item.net_amount);
        const quantity = (0, big_js_1.default)(item.quantity);
        const discount = (0, big_js_1.default)(item.discount);
        item.price = netAmount
            .div(quantity)
            .div((0, big_js_1.default)(1).minus(discount.div(100)))
            .round(decimals)
            .toNumber();
    }
    calculateNetAmount(item, tax, decimals) {
        const quantity = (0, big_js_1.default)(item.quantity || 0);
        const price = (0, big_js_1.default)(item.price || 0);
        const discount = (0, big_js_1.default)(item.discount || 0);
        item.net_amount = quantity
            .mul(price)
            .mul((0, big_js_1.default)(1).minus(discount.div(100)))
            .round(decimals)
            .toNumber();
    }
    calculateNetAmountFromAmount(item, tax, decimals) {
        const amount = (0, big_js_1.default)(item.amount);
        const taxPercentage = (0, big_js_1.default)(tax.rate).plus(1);
        item.net_amount = amount
            .div(taxPercentage.div(100))
            .round(decimals)
            .toNumber();
    }
    calculateAmount(item, tax, decimals) {
        if (item.net_amount === 0) {
            item.amount = 0;
            return;
        }
        if (item.net_amount == null || item.tax_code == null) {
            return;
        }
        const netAmount = (0, big_js_1.default)(item.net_amount);
        item.amount = (0, big_js_1.default)(item.net_amount)
            .mul((0, big_js_1.default)(tax.rate)
            .div(100)
            .plus(1))
            .round(decimals)
            .toNumber();
    }
    recalculateLineItem(item, field) {
        if (this._recalculating) {
            return false;
        }
        try {
            this._recalculating = true;
            log('recalculateLineItem[%s] %o', field, item);
            const currency = this.currency;
            const taxCode = (0, lodash_1.find)(this.taxCodes, { id: item.tax_code });
            if (item.quantity == 0) {
                return;
            }
            switch (field) {
                case 'quantity':
                case 'price':
                case 'discount': {
                    this.calculateNetAmount(item, taxCode, currency.digits);
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'net_amount': {
                    this.calculateUnitPrice(item, taxCode, currency.price_digits);
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'tax_code': {
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'amount': {
                    this.calculateNetAmountFromAmount(item, taxCode, currency.digits);
                    this.calculateUnitPrice(item, taxCode, currency.price_digits);
                    break;
                }
            }
        }
        finally {
            this._recalculating = false;
        }
    }
    applyContractServiceFee(item, service, fee) {
        item.price = fee.amount;
        item.quantity = fee.quantity || 1;
        item.unit = fee.unit;
        this.recalculateLineItem(item, 'quantity');
    }
}
exports.InvoiceLineCalculator = InvoiceLineCalculator;
class InvoiceLineHelper {
    _recalculating = false;
    invoice;
    defaultTaxCode;
    defaultDiscount;
    currencies;
    taxCodes;
    constructor(invoice, currencies, taxCodes) {
        this.invoice = invoice;
        this.defaultTaxCode = this.invoice.meta['default_tax_code'];
        this.defaultDiscount = this.invoice.meta['default_discount'];
        this.currencies = currencies;
        this.taxCodes = taxCodes;
    }
    get isIncomingInvoice() {
        return this.invoice.invoice_kind == 'incoming';
    }
    get currency() {
        const invoice_currency = this.invoice.currency;
        for (let currency of this.currencies) {
            if (currency.code == invoice_currency) {
                return currency;
            }
        }
        throw new handlebars_1.Exception(`Currency not found: ${invoice_currency}`);
    }
    getNearestLine(item) {
        const index = item.order_index == null
            ? this.invoice.lines.data.length - 1
            : item.order_index == 0
                ? 1
                : item.order_index - 1;
        for (let line of this.invoice.lines.data) {
            if (line.order_index == index) {
                return line;
            }
        }
    }
    onProductSelected(item, product) {
        const overrides = {};
        if (!product) {
            product = item.product;
        }
        if ((0, lodash_1.isObject)(product) && product.id == null) {
            product = product.name;
            overrides['product'] = product;
        }
        if ((0, lodash_1.isString)(product)) {
            item.description = product;
            item.tax_code = this.defaultTaxCode;
            const nearestRow = this.getNearestLine(item);
            if (nearestRow) {
                item.unit = nearestRow.unit;
                item.discount = nearestRow.discount;
            }
        }
        else {
            item.product = product;
            item.unit = product.unit;
            item.description = product.name;
            item.tax_code =
                this.defaultTaxCode ||
                    (this.invoice.invoice_kind === 'outgoing'
                        ? product.sales_vat_code
                        : product.purchase_vat_code);
            item.intermediated_service = product.intermediated_service || false;
        }
        if (overrides) {
            (0, lodash_1.extend)(item, overrides);
        }
    }
    calculateUnitPrice(item, tax, decimals) {
        const netAmount = (0, big_js_1.default)(item.net_amount);
        const quantity = (0, big_js_1.default)(item.quantity);
        const discount = (0, big_js_1.default)(item.discount);
        item.price = netAmount
            .div(quantity)
            .div((0, big_js_1.default)(1).minus(discount.div(100)))
            .round(decimals)
            .toNumber();
    }
    calculateNetAmount(item, tax, decimals) {
        const quantity = (0, big_js_1.default)(item.quantity);
        const price = (0, big_js_1.default)(item.price);
        const discount = (0, big_js_1.default)(item.discount);
        item.net_amount = quantity
            .mul(price)
            .mul((0, big_js_1.default)(1).minus(discount.div(100)))
            .round(decimals)
            .toNumber();
    }
    calculateNetAmountFromAmount(item, tax, decimals) {
        const amount = (0, big_js_1.default)(item.amount);
        const taxPercentage = (0, big_js_1.default)(tax.rate).plus(1);
        item.net_amount = amount
            .div(taxPercentage.div(100))
            .round(decimals)
            .toNumber();
    }
    calculateAmount(item, tax, decimals) {
        item.amount = (0, big_js_1.default)(item.net_amount)
            .mul((0, big_js_1.default)(tax.rate)
            .div(100)
            .plus(1))
            .round(decimals)
            .toNumber();
    }
    recalculateLineItem(item, field) {
        if (this._recalculating) {
            return false;
        }
        try {
            this._recalculating = true;
            const currency = this.currency;
            const taxCode = (0, lodash_1.find)(this.taxCodes, { id: item.tax_code });
            if (item.quantity == 0) {
                return;
            }
            switch (field) {
                case 'quantity':
                case 'price':
                case 'discount': {
                    this.calculateNetAmount(item, taxCode, currency.digits);
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'net_amount': {
                    this.calculateUnitPrice(item, taxCode, currency.price_digits);
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'tax_code': {
                    this.calculateAmount(item, taxCode, currency.digits);
                    break;
                }
                case 'amount': {
                    this.calculateNetAmountFromAmount(item, taxCode, currency.digits);
                    this.calculateUnitPrice(item, taxCode, currency.price_digits);
                    break;
                }
            }
        }
        finally {
            this._recalculating = false;
        }
    }
}
exports.InvoiceLineHelper = InvoiceLineHelper;
